import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useShallow } from 'zustand/react/shallow';
import { useSectionStore } from '../../store/sectionStore';
import { applySceneClipping, collectHoleLayerKeys, sectionPlanes } from '../../Utils/sectionMath';
import {
  bakeClipObjectIfNeeded,
  markClipSource,
  setActiveObjectClipBake,
} from '../../Utils/objectClip';
import { getOrigin } from '../../config/siteConfig';

function setRendererClipPlanes(gl, scene, planes) {
  const list = planes || [];
  if (gl) {
    if (!gl.userData) gl.userData = {};
    gl.userData.mdvClipPlanes = list;
  }
  if (scene) {
    if (!scene.userData) scene.userData = {};
    scene.userData.mdvClipPlanes = list;
  }
}

export default function SectionClipper({ modelList, clipRev }) {
  const gl = useThree((s) => s.gl);
  const scene = useThree((s) => s.scene);
  const invalidate = useThree((s) => s.invalidate);
  const dirtyRef = useRef(true);
  const planesRef = useRef([]);

  const {
    enabled,
    orientation,
    centerEast,
    centerNorth,
    centerRl,
    width,
    azimuth,
    clipMode,
    lookFlip,
    boxWidthEast,
    boxWidthNorth,
    boxWidthRl,
    clipHoles,
    clipSolids,
    fencePoints,
    fenceDistance,
    obliqueNormal,
    clipParentUuid,
    clipParentMode,
  } = useSectionStore(
    useShallow((s) => ({
      enabled: s.enabled,
      orientation: s.orientation,
      centerEast: s.centerEast,
      centerNorth: s.centerNorth,
      centerRl: s.centerRl,
      width: s.width,
      azimuth: s.azimuth,
      clipMode: s.clipMode,
      lookFlip: s.lookFlip,
      boxWidthEast: s.boxWidthEast,
      boxWidthNorth: s.boxWidthNorth,
      boxWidthRl: s.boxWidthRl,
      clipHoles: s.clipHoles,
      clipSolids: s.clipSolids,
      fencePoints: s.fencePoints,
      fenceDistance: s.fenceDistance,
      obliqueNormal: s.obliqueNormal,
      clipParentUuid: s.clipParentUuid,
      clipParentMode: s.clipParentMode,
    }))
  );

  const holeKeys = useMemo(() => collectHoleLayerKeys(modelList), [modelList]);
  const state = {
    enabled,
    orientation,
    centerEast,
    centerNorth,
    centerRl,
    width,
    azimuth,
    clipMode,
    lookFlip,
    boxWidthEast,
    boxWidthNorth,
    boxWidthRl,
    fencePoints,
    fenceDistance,
    obliqueNormal,
  };

  useEffect(() => {
    dirtyRef.current = true;
    invalidate();
  }, [
    enabled,
    orientation,
    centerEast,
    centerNorth,
    centerRl,
    width,
    azimuth,
    clipMode,
    lookFlip,
    boxWidthEast,
    boxWidthNorth,
    boxWidthRl,
    clipHoles,
    clipSolids,
    fencePoints,
    fenceDistance,
    obliqueNormal,
    holeKeys,
    modelList,
    clipParentUuid,
    clipParentMode,
    clipRev,
    invalidate,
  ]);

  useEffect(() => {
    if (!gl) return undefined;
    gl.localClippingEnabled = true;
    if (!gl.userData) gl.userData = {};
    return () => {
      gl.clippingPlanes = [];
      setRendererClipPlanes(gl, scene, []);
      applySceneClipping(scene, [], {
        enabled: false,
        clipHoles: true,
        clipSolids: true,
        holeKeys,
      });
    };
  }, [gl, scene, holeKeys]);

  useFrame(() => {
    if (!gl || !scene || !dirtyRef.current) return;
    dirtyRef.current = false;

    const planes = sectionPlanes(state, getOrigin());
    planesRef.current = planes;
    setRendererClipPlanes(gl, scene, planes);

    let objectOn = false;
    if (enabled && clipParentUuid) {
      let mesh = scene.getObjectByProperty('uuid', clipParentUuid);
      if (!mesh) {
        scene.traverse((node) => {
          if (!mesh && node.uuid === clipParentUuid) mesh = node;
        });
      }
      const bake = mesh ? bakeClipObjectIfNeeded(mesh) : null;
      if (!mesh) setActiveObjectClipBake(null);
      markClipSource(scene, bake ? clipParentUuid : null);
      objectOn = Boolean(bake);
      if (bake) {
        const st = useSectionStore.getState();
        if (
          st.clipParentName !== bake.name ||
          st.clipParentClosed !== bake.closed ||
          st.clipParentUsedBox !== bake.usedBox
        ) {
          useSectionStore.setState({
            clipParentName: bake.name,
            clipParentClosed: bake.closed,
            clipParentUsedBox: bake.usedBox,
          });
        }
      }
    } else {
      setActiveObjectClipBake(null);
      markClipSource(scene, null);
    }

    applySceneClipping(scene, planes, {
      enabled,
      clipHoles,
      clipSolids,
      holeKeys,
      objectClipOn: objectOn,
      objectClipMode: clipParentMode,
    });
    gl.clippingPlanes = [];
    gl.localClippingEnabled = true;
  });

  return null;
}
